import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import { Feature } from '@sdk/common/features';
import type { Sections } from '@sdk/components/ui/anchorMenu/createSections';
import useConfig from '@sdk/config/useConfig';

import getPremiumTooltipId from '../premiumGuard/getPremiumTooltipId';
import PremiumGuard from '../premiumGuard/PremiumGuard';
import usePremiumGuard from '../premiumGuard/usePremiumGuard';
import { PremiumModalNames } from '../premiumGuard/constants/modalNames';

import ScheduleSettings from './components/schedule/ScheduleSettings';
import SettingsHeader from './components/SettingsHeader';
import Breaks from './components/schedule/BreaksSection';
import ShiftAcceptance from './components/schedule/ShiftAcceptance';
import ShiftExchanges from './components/schedule/ShiftExchanges';
import Unavailability from './components/schedule/Unavailability';
import LaborCompilance from './components/schedule/LaborCompilance';
import { ShiftFeedback } from './components/schedule/ShiftFeedback';
import { useUserOrgSettings } from './UserOrgSettingsContext';

type Props = {
  orgLocationsCount: number;
  sectionsConfig: Sections;
};

const ScheduleScreen = ({ orgLocationsCount, sectionsConfig }: Props) => {
  const { isOrgFeatureEnabled } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { currentUser } = useAuth();
  const { userOrgSettings } = useUserOrgSettings();

  const {
    FEATURE_FLAGS: { CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED, SHIFT_FEEDBACK_ENABLED },
  } = useConfig();

  const laborComplianceSettings = (
    <>
      <SettingsHeader
        withSeparator
        title={gettext('Labor compliance')}
        ref={sectionsConfig.refs.laborCompliance}
        premiumGuard={getPremiumGuardConfig(Feature.laborCompliance)}
      />
      <LaborCompilance />
    </>
  );

  return (
    <>
      <SettingsHeader
        ref={sectionsConfig.refs.scheduleSettings}
        withSeparator
        title={gettext('Schedule settings')}
      />
      <ScheduleSettings orgLocationsCount={orgLocationsCount} />

      <SettingsHeader ref={sectionsConfig.refs.breaks} title={gettext('Breaks')} />
      <Breaks />

      <PremiumGuard
        premiumGuardModalName={PremiumModalNames.shiftAcceptance}
        hasAccess={isOrgFeatureEnabled(Feature.shiftAcceptance)}
        tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.shiftAcceptance)]}>
        <>
          <SettingsHeader
            ref={sectionsConfig.refs.shiftAcceptance}
            title={gettext('Shift acceptance')}
            premiumGuard={getPremiumGuardConfig(Feature.shiftAcceptance)}
          />
          <ShiftAcceptance />
        </>
      </PremiumGuard>

      <SettingsHeader
        ref={sectionsConfig.refs.shiftExchanges}
        withSeparator
        title={gettext('Shift exchanges and available shifts')}
      />
      <ShiftExchanges />

      {SHIFT_FEEDBACK_ENABLED ? (
        <PremiumGuard
          premiumGuardModalName={PremiumModalNames.shiftFeedback}
          hasAccess={isOrgFeatureEnabled(Feature.shiftFeedback)}
          tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.shiftFeedback)]}>
          <>
            <SettingsHeader
              ref={sectionsConfig.refs.shiftFeedback}
              title={gettext('Shift feedback')}
              premiumGuard={getPremiumGuardConfig(Feature.shiftFeedback)}
              opacity={!currentUser.isAdmin || !userOrgSettings.clockInEnabled ? 0.65 : 1}
            />

            <ShiftFeedback />
          </>
        </PremiumGuard>
      ) : null}

      <Unavailability sectionsConfig={sectionsConfig} />

      {CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED ? (
        <PremiumGuard
          premiumGuardModalName={PremiumModalNames.laborCompliance}
          hasAccess={isOrgFeatureEnabled(Feature.laborCompliance)}
          tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.laborCompliance)]}>
          {laborComplianceSettings}
        </PremiumGuard>
      ) : (
        laborComplianceSettings
      )}
    </>
  );
};

export default ScheduleScreen;
